import Alert from 'components/ui/Alert';
import Breadcrumbs from 'components/v2/Breadcrumbs';
import Container from 'components/v2/container';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import Pagination from 'components/v2/PaginationV2';
import Table from 'components/v2/Table';
import React, { useEffect, useState } from 'react';
import Badge from 'components/v2/Badge';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { showToast } from 'stores/ToastStore';
import { useHistory, useParams } from 'react-router-dom';
import {
  getEmptyNotifyContainersRequests,
  downloadCsvEmptyNotifyRequests,
} from '../../../../utils/WarehouseUserAPIUtils';
import Button from 'components/v2/buttons/Button';
import ChevronLeftIcon from 'components/v2/icons/ChevronLeftIcon';

const CONTAINERS_PER_PAGE = 10;

const EmptyNotifyRequests = () => {
  const { t } = useTranslation();
  const { reportId } = useParams();

  const [page, setPage] = useState(1);
  const [reportItems, setReportItems] = useState([]);
  const [summaryItems, setSummaryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalReportCount, setTotalReportCount] = useState(0);
  
  const history = useHistory();

  const getStatusAndDescription = (data = {}) => {
    let statusColor = 'blue';

    if (!data.processed) {
      return {
        status: t('NOT_PROCESSED'),
        description: t('CONTAINERS_NOT_PROCESSED'),
        statusColor,
      };
    }

    if (data?.remark === 'SUCCESS') {
      const shipmentIds = (data?.meta?.processed_shipments || []).map((s) => s?.shipment_id).join(', ')
      return {
        status: t('SUCCESS'),
        statusColor: 'green',
        description: t('CONTAINER_SUCCESS_MSG', {ids: shipmentIds}),
      };
    }
    if (['FAILED', 'MISSING_USER'].includes(data?.remark)) {
      return {
        status: t('FAILED'),
        statusColor: 'red',
        description: t(data.remark),
      };
    }
    return {
      status: data.processed ? t('FAILED') : t('YET_TO_BE_PROCESSED'),
      description: t(data.remark),
      statusColor: data.processed ? 'red' : 'blue',
    };
  };

  const getSummaryItems = (data = {}, timestamp = '') => {
    let items = [];
    Object.entries(data).forEach(([key, value]) => {
      let type = '';
      switch (key) {
        case 'SUCCESS': {
          type = 'success';
          break;
        }
        case 'FAILED':
        case 'MISSING_USER': {
          type = 'error';
          break;
        }
        default: {
          type = '';
        }
      }

      const titleSlug = `${key}_CONTAINER_TITLE`;
      const messageSlug = `${key}_CONTAINER_MSG`;
      const countKey = `${key}_container_count`.toLowerCase();
      let totalCount = 0;
      if (value && Array.isArray(value) && value.length) {
        value.forEach((subItem) => {
          totalCount += subItem[countKey];
        });
      }
      if (totalCount > 0 || Object.keys(value).length) {
        items.push({
          title: t(titleSlug),
          message: t(messageSlug, {
            count: totalCount > 0 ? totalCount : value[countKey],
            timestamp: key === 'SUCCESS' ? timestamp : '',
          }),
          type,
        });
      }
    });

    return items;
  };

  const fetchAndPopulateEmptyNotifyRequests = async (
    skip = 0,
    limit = CONTAINERS_PER_PAGE,
  ) => {
    try {
      setIsLoading(true);
      const response = await getEmptyNotifyContainersRequests(
        reportId,
        skip,
        limit,
      );
      const result = response.entity;
      if (result?.success) {
        const totalCount = result?.report?.item_count;
        if(totalCount) {
          setTotalReportCount(totalCount)
        }
        const items = result?.report?.items?.map((item) => {
          const container = item?.meta?.container_number;
          const shipmentIds = item?.meta?.processed_shipments || [];
          const {
            status,
            description = '',
            statusColor = '',
          } = getStatusAndDescription(item);
          return { container, status, description, shipmentIds, statusColor };
        });
        setReportItems(items);
        
        const timestamp = moment
          .tz(result?.report?.created_at, DEFAULT_TIMEZONE)
          .format('L LT');

        setSummaryItems(getSummaryItems(result?.summary, timestamp));
      }
    } catch (e) {
      console.log('eee',e)
      showToast('', t('FETCH_EMPTY_NOTIFY_FAILED'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const skip = (page - 1) * CONTAINERS_PER_PAGE;
    fetchAndPopulateEmptyNotifyRequests(skip);
  }, [page]);

  const breadcrumbs = {
    pages: [
      {
        name: t('EMPTY_NOTIFY_PORTAL'),
        href: `/`,
        current: true,
      },
    ],
  };

  const columns = [
    {
      field: 'container',
    },
    {
      field: 'status',
      render: (value, row) => {
        return (
          <Badge withRemove color={row?.statusColor}>
            {value}
          </Badge>
        );
      },
    },
    { field: 'description' },
  ];

  const headers = [
    {
      label: t('CONTAINER_NUMBER'),
    },
    { label: t('STATUS') },
    { label: t('DESCRIPTION') },
  ];

  const pageCount = Math.ceil(totalReportCount / CONTAINERS_PER_PAGE);

  const footer = () => {
    return (
      <tr className='m-0 p-0'>
        <td className='m-0 p-0' colSpan={3}>
          <Pagination
            perPage={CONTAINERS_PER_PAGE}
            page={page}
            pages={pageCount}
            total={totalReportCount}
            showCount
            onPageClick={(page) => setPage(page)}
          />
        </td>
      </tr>
    );
  };

  const renderAlerts = (bannerItems = []) => {
    return bannerItems.map(({ title, message, type }, i) => (
      <Alert key={i} className="mb-2" title={title} type={type}>
        {message}
      </Alert>
    ));
  };

  const navigateToRequestPage = () => {
    history.push('/warehouse/empty-notify')

  }
  return (
    <Container className="lg:mt-0 my-4">
      <Breadcrumbs className="mt-4" breadcrumbs={breadcrumbs} />
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <div className="relative">
          {renderAlerts(summaryItems)}
          <div className="flex justify-end items-center my-2">
            <button
              type="button"
              className="button button-hollow-green my-2"
              onClick={() => downloadCsvEmptyNotifyRequests(reportId)}
            >
              <i className="fa fa-file-excel-o mr-2" />
              {t('EXPORT_TO_CSV')}
            </button>
          </div>

          <Table
            headers={headers}
            columns={columns}
            footer={footer}
            data={reportItems}
          />
          <Button className={'mb-8 mt-2 float-right flex justify-center items-center'} onClick={navigateToRequestPage}>
            <ChevronLeftIcon className='w-4 h-4 mr-1' />  {t('START_AGAIN')}</Button>
        </div>
      )}
    </Container>
  );
};

export default EmptyNotifyRequests;
